import { useState } from 'react';
import {
  apiGoLive,
  apiPausePayments,
  apiResumePayments,
  type PaymentsChecklistItem,
  type PaymentsSettings,
} from '~/lib/api/payments';
import { classNames } from '~/utils/classNames';
import { apiErrorMessage, checklistItemState, summarizeChecklist, type ChecklistState } from './payments-model';
import { BTN_GHOST, BTN_SECONDARY, CARD, ErrorBanner, Section, Spinner } from './ui';

const ITEM_ICON: Record<ChecklistState, string> = {
  ok: 'i-ph:check-circle-fill text-success',
  blocking: 'i-ph:x-circle-fill text-danger',
  advisory: 'i-ph:warning-circle-fill text-warning',
};

const ITEM_LABEL: Record<ChecklistState, string> = { ok: 'Done', blocking: 'Required', advisory: 'Recommended' };

export function GoLiveCard({
  appId,
  settings,
  checklist,
  onChanged,
}: {
  appId: string;
  settings: PaymentsSettings;
  checklist: PaymentsChecklistItem[];
  onChanged: () => void;
}) {
  // The go-live/resume response carries a fresher checklist than the overview.
  const [latest, setLatest] = useState<PaymentsChecklistItem[] | null>(null);
  const [busy, setBusy] = useState<'live' | 'pause' | 'resume' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pausing, setPausing] = useState(false);
  const [reason, setReason] = useState('');
  const items = latest ?? checklist;
  const { canGoLive, blockingLeft } = summarizeChecklist(items);
  const state = settings.liveState;

  const run = async (kind: 'live' | 'pause' | 'resume') => {
    setBusy(kind);
    setError(null);

    try {
      if (kind === 'pause') {
        await apiPausePayments(appId, reason.trim() || undefined);
        setPausing(false);
        setReason('');
      } else {
        const res = kind === 'live' ? await apiGoLive(appId) : await apiResumePayments(appId);
        setLatest(res.checklist);

        if (!res.ok) {
          setError('Some required steps are still open.');
          return;
        }
      }

      setLatest(null);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Section title="Go live" icon="i-ph:rocket-launch">
      <div className={classNames(CARD, 'overflow-hidden')}>
        <ul className="divide-y divide-hairline">
          {items.map((item) => {
            const s = checklistItemState(item);

            return (
              <li key={item.id} className="flex items-start gap-2.5 px-4 py-2.5">
                <div className={classNames(ITEM_ICON[s], 'mt-px shrink-0 text-sm')} aria-label={ITEM_LABEL[s]} />
                <span
                  className={classNames('text-[12px] leading-relaxed', s === 'ok' ? 'text-secondary' : 'text-primary')}
                >
                  {item.message}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="space-y-3 border-t border-hairline px-4 py-3">
          {error && <ErrorBanner message={error} />}

          {state === 'test' && (
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => run('live')}
                disabled={!canGoLive || busy !== null}
                className="ui-btn-accent !px-4 !py-2 !text-[12px]"
              >
                {busy === 'live' ? <Spinner /> : <div className="i-ph:lightning text-[13px]" />}
                Go live
              </button>
              {!canGoLive && (
                <span className="text-[11px] text-muted">
                  {blockingLeft === 1 ? '1 required step left' : `${blockingLeft} required steps left`}
                </span>
              )}
            </div>
          )}

          {state === 'live' &&
            (pausing ? (
              <div className="space-y-2">
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (optional), e.g. Out of stock"
                  maxLength={200}
                  className="ui-input"
                />
                <div className="flex items-center gap-2">
                  <button onClick={() => run('pause')} disabled={busy !== null} className={BTN_SECONDARY}>
                    {busy === 'pause' && <Spinner />}
                    Pause live payments
                  </button>
                  <button onClick={() => setPausing(false)} disabled={busy !== null} className={BTN_GHOST}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-[12px] text-secondary">Your site is taking real payments.</span>
                <button onClick={() => setPausing(true)} className={BTN_SECONDARY}>
                  <div className="i-ph:pause text-[13px]" />
                  Pause live payments
                </button>
              </div>
            ))}

          {state === 'paused' && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-[12px] text-secondary">Checkout is off on your live site until you resume.</span>
              <button onClick={() => run('resume')} disabled={!canGoLive || busy !== null} className={BTN_SECONDARY}>
                {busy === 'resume' ? <Spinner /> : <div className="i-ph:play text-[13px]" />}
                Resume
              </button>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}
